import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { DatasetSection } from '../../lib/api';

interface SectionEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  section: Partial<DatasetSection>;
  onChange: (section: Partial<DatasetSection>) => void;
  onSave: () => void;
  isEditing: boolean;
}

export default function SectionEditor({
  open,
  onOpenChange,
  section,
  onChange,
  onSave,
  isEditing,
}: SectionEditorProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Section' : 'Add Section'}</DialogTitle>
          <DialogDescription>
            Sections help organize your questionnaire into logical groups
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="sectionTitle">Section Title *</Label>
            <Input
              id="sectionTitle"
              value={section.title || ''}
              onChange={(e) => onChange({ ...section, title: e.target.value })}
              placeholder="e.g., Company Information"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="sectionDesc">Description</Label>
            <Textarea
              id="sectionDesc"
              value={section.description || ''}
              onChange={(e) => onChange({ ...section, description: e.target.value })}
              placeholder="Brief description of this section..."
              rows={2}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label>Required Section</Label>
              <p className="text-sm text-muted-foreground">
                Must complete all required questions in this section
              </p>
            </div>
            <Switch
              checked={section.is_required ?? true}
              onCheckedChange={(checked) => onChange({ ...section, is_required: checked })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="sectionWeight">Weight (for scoring)</Label>
            <Input
              id="sectionWeight"
              type="number"
              min="0"
              max="100"
              value={section.weight || ''}
              onChange={(e) => onChange({ ...section, weight: e.target.value ? Number(e.target.value) : undefined })}
              placeholder="Leave empty for equal weighting"
            />
            <p className="text-sm text-muted-foreground">
              Higher weight means this section contributes more to the overall score
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave}>
            {isEditing ? 'Save Changes' : 'Add Section'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
